/**
 * Browser → our-server upload engine for YouTube videos.
 *
 * The upload target is *our* `/api/youtube/upload/bytes/:uploadId`, NOT
 * YouTube's resumable session URL. Reasoning: YouTube's session URL
 * doesn't return CORS headers compatible with browser PUT requests that
 * carry `Content-Type: video/*`, so the browser preflight fails. Our
 * server proxies the bytes through to YouTube using a Node 20+ streaming
 * fetch (no buffering), so the data flow is:
 *
 *   browser → vedaunion server (in transit only, ~64KB at a time) → YouTube
 *
 * Why XHR over fetch: `fetch` doesn't expose request-side progress events
 * for upload bodies (the readable-stream upload progress proposal hasn't
 * shipped in Chrome stable yet). XHR gives us `xhr.upload.onprogress`.
 *
 * v1 limitation: no automatic resume across tab close. If the upload
 * fails mid-stream (network blip, server reboot), the user picks the file
 * again and starts over. The session URL stays valid on YouTube's side
 * for 24h, but resuming from a partial offset would need server-side
 * orchestration we don't have yet.
 */

export interface UploadProgress {
  bytesUploaded: number;
  totalBytes: number;
  ratio: number; // 0..1
  speedBps: number; // bytes per second (over the most recent ~3s)
  etaSec: number | null;
}

export interface UploadHandle {
  abort: () => void;
  /** Resolves with the YouTube videoId on success. */
  promise: Promise<string>;
}

interface UploadOptions {
  /** Server endpoint that proxies bytes to YouTube. */
  uploadUrl: string;
  file: File | Blob;
  contentType: string;
  totalBytes: number;
  onProgress?: (p: UploadProgress) => void;
}

/**
 * POST the file body to our server's upload-bytes endpoint, with progress
 * events surfaced via XHR. The server streams the bytes to YouTube and
 * returns `{ ok: true, videoId, playlistResults }`.
 */
export function startUpload(opts: UploadOptions): UploadHandle {
  const xhr = new XMLHttpRequest();

  // Sliding-window speed sampler — keeps the ETA responsive without
  // jitter from each onprogress tick.
  const samples: Array<{ t: number; bytes: number }> = [];
  const SAMPLE_WINDOW_MS = 3_000;

  const promise = new Promise<string>((resolve, reject) => {
    xhr.open("POST", opts.uploadUrl, true);
    xhr.setRequestHeader("Content-Type", opts.contentType);
    // Same-origin to our server, so cookies travel automatically. No need
    // to set X-CSRF or similar — Hono's CSRF middleware accepts the Origin
    // header set by the browser for our app.
    xhr.withCredentials = true;

    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return;
      const bytesUploaded = e.loaded;
      const now = performance.now();
      samples.push({ t: now, bytes: bytesUploaded });
      while (samples.length > 1 && now - samples[0]!.t > SAMPLE_WINDOW_MS) {
        samples.shift();
      }
      const first = samples[0]!;
      const last = samples[samples.length - 1]!;
      const dt = (last.t - first.t) / 1000;
      const speedBps = dt > 0 ? (last.bytes - first.bytes) / dt : 0;
      const remaining = opts.totalBytes - bytesUploaded;
      const etaSec = speedBps > 0 ? remaining / speedBps : null;
      opts.onProgress?.({
        bytesUploaded,
        totalBytes: opts.totalBytes,
        ratio: bytesUploaded / opts.totalBytes,
        speedBps,
        etaSec,
      });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const body = JSON.parse(xhr.responseText) as { videoId?: string };
          if (body.videoId) {
            opts.onProgress?.({
              bytesUploaded: opts.totalBytes,
              totalBytes: opts.totalBytes,
              ratio: 1,
              speedBps: 0,
              etaSec: 0,
            });
            resolve(body.videoId);
            return;
          }
          reject(new UploadError("Server returned no videoId", xhr.status));
        } catch {
          reject(
            new UploadError("Server returned malformed JSON", xhr.status),
          );
        }
        return;
      }
      // Try to surface the server's error message.
      let serverMsg = "";
      try {
        const body = JSON.parse(xhr.responseText) as { error?: string };
        if (body.error) serverMsg = body.error;
      } catch {
        serverMsg = xhr.responseText.slice(0, 200);
      }
      reject(
        new UploadError(
          serverMsg || `Upload failed (${xhr.status})`,
          xhr.status,
        ),
      );
    };

    xhr.onerror = () =>
      reject(new UploadError("Network error during upload", 0));
    xhr.onabort = () =>
      reject(new UploadError("Upload aborted by user", 0, true));

    xhr.send(opts.file);
  });

  return {
    abort: () => {
      try {
        xhr.abort();
      } catch {
        /* */
      }
    },
    promise,
  };
}

export class UploadError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly userAborted = false,
  ) {
    super(message);
    this.name = "UploadError";
  }
}

// -----------------------------------------------------------------------------
// Display helpers
// -----------------------------------------------------------------------------

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatEta(sec: number | null): string {
  if (sec === null || !Number.isFinite(sec)) return "—";
  if (sec < 60) return `${Math.ceil(sec)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${Math.ceil(sec % 60)}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
}
